
import { useState } from 'react';
import { useReports } from './hooks/useReports';
import { generateSchedulePayload } from './lib/scheduler';

export default function ScheduledReports() {
  const { scheduled } = useReports();
  const [form, setForm] = useState({
    type: 'finance',
    recipients: '',
    frequency: 'monthly'
  });
  
  const [schedules, setSchedules] = useState([
    {
      id: 1,
      type: 'finance',
      name: 'Monthly Finance Report',
      frequency: 'monthly',
      recipients: ['finance@princejewelry.com'],
      enabled: true,
      nextRun: '2024-02-01T09:00:00Z',
      lastRun: '2024-01-01T09:00:00Z'
    },
    {
      id: 2,
      type: 'gst',
      name: 'GST Compliance Report',
      frequency: 'monthly',
      recipients: ['admin@princejewelry.com', 'finance@princejewelry.com'],
      enabled: true,
      nextRun: '2024-02-01T10:00:00Z',
      lastRun: '2024-01-01T10:00:00Z'
    },
    {
      id: 3,
      type: 'vendor',
      name: 'Vendor Performance Report',
      frequency: 'weekly',
      recipients: ['ops@princejewelry.com'],
      enabled: false,
      nextRun: '2024-01-22T08:00:00Z',
      lastRun: '2024-01-15T08:00:00Z'
    }
  ]);
  
  const handleCreateSchedule = async () => {
    try {
      const recipientList = form.recipients.split(',').map(r => r.trim()).filter(r => r);
      const payload = generateSchedulePayload(form.type, recipientList, form.frequency as any, {});
      
      await scheduled.mutateAsync(payload);
      
      // Add to local state (in real app, refetch from server)
      const newId = Math.max(...schedules.map(s => s.id)) + 1;
      const newSchedule = {
        id: newId,
        type: form.type,
        name: `${form.type.charAt(0).toUpperCase() + form.type.slice(1)} Report`,
        frequency: form.frequency,
        recipients: recipientList,
        enabled: true,
        nextRun: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // Tomorrow
        lastRun: new Date().toISOString()
      };
      
      setSchedules([...schedules, newSchedule]);
      
      // Reset form
      setForm({
        type: 'finance',
        recipients: '',
        frequency: 'monthly'
      });
    } catch (error) {
      console.error('Failed to create schedule:', error);
    }
  };
  
  const toggleSchedule = (id: number) => {
    setSchedules(schedules.map(s => 
      s.id === id ? { ...s, enabled: !s.enabled } : s
    ));
  };
  
  const deleteSchedule = (id: number) => {
    setSchedules(schedules.filter(s => s.id !== id));
  };
  
  return (
    <div className="p-6 space-y-4">
      <h1 className="text-2xl font-semibold text-[var(--color-primary)]">Scheduled Reports</h1>
      
      {/* Create New Schedule Form */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <h2 className="text-lg font-semibold mb-4">Create New Schedule</h2>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Report Type</label>
            <select 
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]" 
              value={form.type} 
              onChange={e => setForm({...form, type: e.target.value})}
            >
              <option value="finance">Finance</option>
              <option value="gst">GST</option>
              <option value="vendor">Vendor</option>
              <option value="compliance">Compliance</option>
              <option value="audit">Audit</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Recipients</label>
            <input 
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]" 
              placeholder="email1@example.com, email2@example.com" 
              value={form.recipients}
              onChange={e => setForm({...form, recipients: e.target.value})}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Frequency</label>
            <select 
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]" 
              value={form.frequency} 
              onChange={e => setForm({...form, frequency: e.target.value})}
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
        </div>
        
        <div className="mt-4">
          <button 
            onClick={handleCreateSchedule}
            disabled={!form.recipients.trim()}
            className="px-4 py-2 bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-primary)]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
          >
            Create Schedule
          </button>
        </div>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-600">Total Schedules</h3>
              <p className="text-2xl font-bold text-[var(--color-primary)]">{schedules.length}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <i className="ri-calendar-line text-xl text-blue-600"></i>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-600">Active Schedules</h3>
              <p className="text-2xl font-bold text-[var(--color-accent)]">
                {schedules.filter(s => s.enabled).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <i className="ri-play-circle-line text-xl text-green-600"></i>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-600">Monthly Reports</h3>
              <p className="text-2xl font-bold text-[var(--color-secondary)]">
                {schedules.filter(s => s.frequency === 'monthly').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
              <i className="ri-time-line text-xl text-purple-600"></i>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-600">Recipients</h3>
              <p className="text-2xl font-bold text-orange-600">
                {new Set(schedules.flatMap(s => s.recipients)).size}
              </p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
              <i className="ri-mail-line text-xl text-orange-600"></i>
            </div>
          </div>
        </div>
      </div>
      
      {/* Scheduled Reports List */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-semibold">Current Schedules</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[var(--color-accent)]/10">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Report Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Frequency</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Recipients</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Next Run</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {schedules.map((schedule) => (
                <tr key={schedule.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {schedule.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                      {schedule.type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                    {schedule.frequency}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    <div className="max-w-xs">
                      {schedule.recipients.slice(0, 2).map((email, i) => (
                        <div key={i} className="truncate">{email}</div>
                      ))}
                      {schedule.recipients.length > 2 && (
                        <div className="text-xs text-gray-400">
                          +{schedule.recipients.length - 2} more
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(schedule.nextRun).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      schedule.enabled 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {schedule.enabled ? 'Active' : 'Paused'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => toggleSchedule(schedule.id)}
                        className={`p-1 rounded hover:bg-gray-100 cursor-pointer ${
                          schedule.enabled ? 'text-orange-600' : 'text-green-600'
                        }`}
                        title={schedule.enabled ? 'Pause' : 'Resume'}
                      >
                        <i className={`ri-${schedule.enabled ? 'pause' : 'play'}-line`}></i>
                      </button>
                      <button
                        onClick={() => deleteSchedule(schedule.id)}
                        className="p-1 rounded hover:bg-gray-100 text-red-600 cursor-pointer"
                        title="Delete"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {schedules.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-500">No scheduled reports configured.</div>
            <div className="mt-2 text-[var(--color-accent)]">
              Create your first schedule using the form above
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
